import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Link,
  Button
} from "@mui/material";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import parse from "html-react-parser";

export default function Help() {
  const [open, setOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [matches, setMatches] = useState([]);
  const [showBackButton, setShowBackButton] = useState(false);
  const contentRef = useRef(null);

  // Load external HTML when dialog opens
  useEffect(() => {
    if (open) {
        // Append a timestamp query string to force reload
        fetch(process.env.PUBLIC_URL + "/manual.html?nocache=" + Date.now())
        .then((res) => {
            if (!res.ok) throw new Error("manual.html not found");
            return res.text();
        })
        .then((data) => setHtmlContent(data))
        .catch((err) => {
            console.error("Error loading manual:", err);
            setHtmlContent("<p>Manual could not be loaded.</p>");
        });
    }
    }, [open]);

  // Only build regex if searchTerm length >= 2
  const regex =
    searchTerm.length >= 2 ? new RegExp(`(${searchTerm})`, "gi") : null;

  // Parse HTML and inject IDs for matches
  const parsedContent = useMemo(() => {
    let matchList = [];
    let matchCounter = 0;

    if (!regex) {
      setMatches([]);
      return parse(htmlContent);
    }

    const content = parse(htmlContent, {
      replace: (node) => {
        if (node.type === "text" && regex) {
          const parts = node.data.split(regex);
          if (parts.length > 1) {
            return (
              <>
                {parts.map((part, i) => {
                  if (regex.test(part)) {
                    const id = `match-${matchCounter++}`;
                    const originalText = node.data;
                    const matchIndex = originalText.indexOf(part);

                    // Extend to 20 characters before and after
                    const start = Math.max(0, matchIndex - 20);
                    const end = Math.min(
                      originalText.length,
                      matchIndex + part.length + 20
                    );
                    let preview = originalText.slice(start, end);

                    // Add ellipses if truncated
                    if (start > 0) preview = "…" + preview;
                    if (end < originalText.length) preview = preview + "…";

                    // Bold the matched keyword inside the preview
                    const previewWithBold = preview.replace(
                      regex,
                      (match) => `<b>${match}</b>`
                    );

                    matchList.push({
                      id,
                      preview: previewWithBold
                    });

                    return (
                      <span key={i} id={id}>
                        {part}
                      </span>
                    );
                  }
                  return part;
                })}
              </>
            );
          }
        }
      }
    });

    setMatches(matchList);
    return content;
  }, [htmlContent, searchTerm]);

  const handleClear = () => {
    setSearchTerm("");
    setMatches([]);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0; // jump instantly to top
    }
  };

  const handleBackToResults = () => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  // Track scroll position to enable/disable Back button
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const onScroll = () => {
      setShowBackButton(el.scrollTop > 0); // enable if scrolled down
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [contentRef]);

  return (
    <>
      <Fab
        color="#86BBD8"
        size="small"
        aria-label="help"
        onClick={() => setOpen(true)}
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          opacity: 0.5,
          zIndex: (theme) => theme.zIndex.tooltip + 1
        }}
      >
        <QuestionMarkIcon />
      </Fab>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <TextField
            size="small"
            placeholder="Find..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm && (
                    <IconButton size="small" onClick={handleClear}>
                      <ClearIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              )
            }}
            fullWidth
          />
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
        dividers
        ref={contentRef}
        onScroll={(e) => {
            const el = e.currentTarget;
            setShowBackButton(el.scrollTop > 0);
        }}
        >
        {matches.length > 0 && (
            <List dense>
            {matches.map((m, i) => (
                <ListItem key={i}>
                <Link
                    href={`#${m.id}`}
                    dangerouslySetInnerHTML={{ __html: m.preview }}
                />
                </ListItem>
            ))}
            </List>
        )}

        {parsedContent}
        </DialogContent>

        {matches.length > 0 && (
        <DialogActions sx={{ justifyContent: "flex-start" }}>
            <Button
            size="small"
            onClick={handleBackToResults}
            disabled={!showBackButton}
            >
            Back to Results
            </Button>
        </DialogActions>
        )}
      </Dialog>
    </>
  );
}